import { compact } from "lodash";
import { ReactElement } from "react";
import { ArtifactType, GoalScope, GoalState } from "types/graphql-schema";

import { getLabel } from "@apps/use-label/use-label";
import { getUiPreferenceCache } from "@apps/use-ui-preference-cache/use-ui-preference-cache";
import { currentOrganizationVar, currentUserVar } from "@cache/cache";
import { assertEdgesNonNull } from "@helpers/helpers";

import {
  ExplorerFilterType,
  explorerUserType,
  getDefaultFilters,
} from "../helpers";
import { BookmarkFilterEnum } from "./bookmark-constants";
import ExplorerBookmarkIcon from "./bookmark-icon";

export type ExplorerBookmarkLinks = {
  title: string;
  filters: ExplorerFilterType;
  showInSlashCommand: boolean;
  default: boolean;
}[];

export const getExplorerBookmarks = (
  bookmarkFilter: BookmarkFilterEnum | null = null,
) => {
  const label = getLabel();
  const currentUser = currentUserVar();
  const currentOrganization = currentOrganizationVar();
  const uiPreferenceCache = getUiPreferenceCache();
  const defaultFilters = getDefaultFilters();
  const userExplorerBookmarks = assertEdgesNonNull(
    currentUser.explorerBookmarks,
  ).map((bookmark) => ({ ...bookmark, default: false }));

  const defaultBookmarks = compact([
    currentOrganization.featureFlags.actionItems && {
      filters: { type: ArtifactType.ActionItem },
      title: `All action items`,
    },
    ...(currentOrganization.featureFlags.actionItems
      ? compact(currentOrganization.actionItemStates).map(
          (actionItemState) => ({
            filters: {
              type: ArtifactType.ActionItem,
              actionItemAssignee: currentUser.id,
              actionItemState: actionItemState.value,
            },
            title: `My "${actionItemState.label}" action items`,
          }),
        )
      : []),
    currentOrganization.featureFlags.goals && {
      filters: {
        ...defaultFilters,
        type: ArtifactType.Goal,
      },
      title: `All ${label("goal", { pluralize: true })}`,
    },
    currentOrganization.featureFlags.goals && {
      filters: {
        type: ArtifactType.Goal,
        goalOwners: [currentUser.id],
        goalState: [GoalState.Open],
      },
      title: `My opened ${label("goal", { pluralize: true })}`,
    },
    currentOrganization.featureFlags.goals && {
      filters: {
        type: ArtifactType.Goal,
        goalOwners: [currentUser.id],
        goalState: [GoalState.Draft],
      },
      title: `My draft ${label("goal", { pluralize: true })}`,
    },
    currentOrganization.featureFlags.goals && {
      filters: {
        type: ArtifactType.Goal,
        goalScope: GoalScope.Organization,
        goalState: [GoalState.Open],
      },
      title: `Opened  ${label("organization")} ${label("goal", {
        pluralize: true,
      })}`,
    },
    currentOrganization.featureFlags.recognitions && {
      filters: { type: ArtifactType.Recognition },
      title: `All ${label("recognition", {
        pluralize: true,
        capitalize: false,
      })}`,
    },
    currentOrganization.featureFlags.recognitions && {
      filters: {
        type: ArtifactType.Recognition,
        recognitionRecipient: currentUser.id,
      },
      title: `My ${label("recognition", {
        pluralize: true,
        capitalize: false,
      })}`,
    },
    currentOrganization.featureFlags.feedbacks && {
      filters: {
        type: ArtifactType.Feedback,
        feedbackRecipient: currentUser.id,
      },
      title: `${label("feedback", {
        pluralize: true,
        capitalize: true,
      })} I received`,
    },
    {
      filters: { type: explorerUserType },
      title: `All users`,
    },
  ]).map((bookmark) => ({
    ...bookmark,
    filters: JSON.stringify({
      ...defaultFilters,
      ...bookmark.filters,
    }),
    showInSlashCommand: true,
    private: false,
    default: true,
  }));

  const explorerBookmarks = [
    ...userExplorerBookmarks,
    ...defaultBookmarks,
  ].filter(
    (bookmark) =>
      bookmarkFilter === null ||
      (bookmarkFilter === BookmarkFilterEnum.onlyYou &&
        bookmark.private &&
        !bookmark.default) ||
      (bookmarkFilter === BookmarkFilterEnum.default && bookmark.default) ||
      (bookmarkFilter === BookmarkFilterEnum.organization &&
        !bookmark.private &&
        !bookmark.default),
  );

  const bookmarksGroupedByType = explorerBookmarks.reduce(
    (memo, bookmark) => {
      const filters = JSON.parse(bookmark.filters);
      const key = filters.type || "search";
      if (!memo[key]) {
        memo[key] = {
          id: key,
          label: label(key, { capitalize: true }),
          icon: <ExplorerBookmarkIcon type={key} />,
          links: [],
          expanded: !uiPreferenceCache.explorerCollapsedGroupIds.includes(key),
        };
      }
      memo[key].links = [
        ...memo[key].links,
        {
          title: bookmark.title,
          filters,
          showInSlashCommand: true,
          default: false,
        },
      ];
      return memo;
    },
    {} as {
      [key: string]: {
        id: string;
        label: string;
        icon: ReactElement;
        expanded: boolean;
        links: ExplorerBookmarkLinks;
      };
    },
  );
  return Object.values(bookmarksGroupedByType);
};
